import { FloatingOverlay, FloatingPortal } from "@floating-ui/react";
import {
  createContext,
  PropsWithChildren,
  useCallback,
  useContext,
  useState,
} from "react";
import styled from "styled-components";
import { Button, ButtonProps } from "../button";
import { Icon } from "../icons";

type DialogContextType = {
  isOpen: boolean;
  open: () => void;
  close: () => void;
};
const DialogContext = createContext<DialogContextType>({} as DialogContextType);

type DialogProps = PropsWithChildren<{
  onClose?: () => void;
  trigger: JSX.Element;
}>;

export const Dialog = ({ children, trigger }: DialogProps) => {
  const [isOpen, setIsOpen] = useState(false);
  const close = useCallback(() => setIsOpen(false), []);
  const open = useCallback(() => setIsOpen(true), []);

  return (
    <DialogContext.Provider value={{ isOpen, close, open }}>
      {trigger}
      {isOpen && (
        <>
          <DialogBackdrop onClick={close} />
          <FloatingPortal>
            <DialogWrapper onClick={(e) => e.stopPropagation()}>
              {children}
            </DialogWrapper>
          </FloatingPortal>
        </>
      )}
    </DialogContext.Provider>
  );
};

const DialogTrigger = ({
  children,
  ...props
}: PropsWithChildren<ButtonProps>) => {
  const { open } = useContext(DialogContext);
  return (
    <Button {...props} onClick={open}>
      {children}
    </Button>
  );
};

const DialogTitle = ({ children }: PropsWithChildren) => {
  const { close } = useContext(DialogContext);
  return (
    <FlexHorizontal>
      {children}
      <Button variant="neutral" onClick={close}>
        <Icon.Close />
      </Button>
    </FlexHorizontal>
  );
};

const DialogContent = ({ children }: PropsWithChildren) => {
  return children;
};

const DialogActions = ({ children }: PropsWithChildren) => {
  return <DialogActionsWrapper>{children}</DialogActionsWrapper>;
};

const DialogCancel = ({ children }: PropsWithChildren) => {
  const { close } = useContext(DialogContext);
  return (
    <Button variant="secondary" onClick={close}>
      {children}
    </Button>
  );
};

const DialogConfirm = ({
  children,
  onClick,
}: PropsWithChildren<{ onClick?: () => void }>) => {
  const { close } = useContext(DialogContext);
  return (
    <Button
      variant="primary"
      onClick={() => {
        onClick?.();
        close();
      }}
    >
      {children}
    </Button>
  );
};

const DialogBackdrop = styled(FloatingOverlay)`
  background-color: #00000066;
  // We need some z-index here, because of legacy components in Mindlercare
  z-index: 1000;
`;

const FlexHorizontal = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const DialogActionsWrapper = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  margin-bottom: ${(props) => props.theme.SPACING.LG};
`;

const DialogWrapper = styled.div`
  position: fixed;
  // We should not need some crazy z-index here, just a bit more than backdrop,
  // because this component should render outside of the root, in the portal.
  z-index: 1001;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  background: white;
  box-shadow: 0px 2px 8px 0px #1d1b1b33;
  max-width: 704px; // this is magic width from the design doc.
  padding: ${(props) => props.theme.SPACING.MD}
    ${(props) => `calc(${props.theme.SPACING.LG} + ${props.theme.SPACING.MD})`};
`;

Dialog.Trigger = DialogTrigger;
Dialog.Title = DialogTitle;
Dialog.Content = DialogContent;
Dialog.Actions = DialogActions;
Dialog.Cancel = DialogCancel;
Dialog.Confirm = DialogConfirm;
